import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats, limpiarSesionesCaducadas, clearDashboardError } from '../../redux/dashboardSlice';
import { Card, Row, Col, Alert, Placeholder, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartBar,
  faNewspaper,
  faGift,
  faClock,
  faTrashAlt,
  faUserShield,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

const DashboardPage = () => {
  const dispatch = useDispatch();
  const { stats, loading, error, cleaningSessions } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    return () => {
      dispatch(clearDashboardError());
    };
  }, [dispatch]);

  const handleCleanSessions = () => {
    dispatch(limpiarSesionesCaducadas());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return moment(dateString).format('LL [a las] h:mm a');
  };

  if (error) {
    return (
      <div className="mt-4">
        <Alert variant="danger" onClose={() => dispatch(clearDashboardError())} dismissible>
          Error: {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h1 className="mb-4">
        <FontAwesomeIcon icon={faChartBar} className="me-2" />
        Dashboard General
      </h1>
      
      {loading ? (
        <Row>
          {Array.from({ length: 5 }).map((_, index) => (
            <Col key={index} md={4} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <Placeholder as={Card.Title} animation="glow">
                    <Placeholder xs={6} />
                  </Placeholder>
                  <Placeholder as={Card.Text} animation="glow">
                    <Placeholder xs={7} /> <br />
                    <Placeholder xs={4} /> <br />
                    <Placeholder xs={6} /> <br />
                    <Placeholder xs={5} />
                  </Placeholder>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        stats && (
          <Row>
            {/* Usuarios */}
            <Col md={4} className="mb-4">
              <Card className="h-100 shadow-sm border-primary">
                <Card.Header className="bg-primary text-white">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  <strong>Usuarios</strong>
                </Card.Header>
                <Card.Body>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <Badge bg="primary" className="me-2">
                        {stats.usuarios.total}
                      </Badge>
                      Total
                    </li>
                    <li className="mb-2">
                      <Badge bg="success" className="me-2">
                        {stats.usuarios.activos}
                      </Badge>
                      Activos
                    </li>
                    <li className="mb-2">
                      <FontAwesomeIcon icon={faUserShield} className="me-2 text-warning" />
                      <Badge bg="warning" text="dark" className="me-2">
                        {stats.usuarios.administradores}
                      </Badge>
                      Administradores
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faUserTie} className="me-2 text-info" />
                      <Badge bg="info" className="me-2">
                        {stats.usuarios.socios}
                      </Badge>
                      Socios
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Encuestas */}
            <Col md={4} className="mb-4">
              <Card className="h-100 shadow-sm border-success">
                <Card.Header className="bg-success text-white">
                  <FontAwesomeIcon icon={faChartBar} className="me-2" />
                  <strong>Encuestas</strong>
                </Card.Header>
                <Card.Body>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <Badge bg="primary" className="me-2">
                        {stats.encuestas.total}
                      </Badge>
                      Total
                    </li>
                    <li className="mb-2">
                      <Badge bg="success" className="me-2">
                        {stats.encuestas.activas}
                      </Badge>
                      Activas
                    </li>
                    <li>
                      <Badge bg="info" className="me-2">
                        {stats.encuestas.respuestasTotales}
                      </Badge>
                      Respuestas Totales
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Noticias */}
            <Col md={4} className="mb-4">
              <Card className="h-100 shadow-sm border-info">
                <Card.Header className="bg-info text-white">
                  <FontAwesomeIcon icon={faNewspaper} className="me-2" />
                  <strong>Noticias</strong>
                </Card.Header>
                <Card.Body>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <Badge bg="primary" className="me-2">
                        {stats.noticias.total}
                      </Badge>
                      Total
                    </li>
                    <li>
                      <Badge bg="secondary" className="me-2">
                        <FontAwesomeIcon icon={faClock} />
                      </Badge>
                      Última Publicación: {formatDate(stats.noticias.ultimaPublicacion)}
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Beneficios */}
            <Col md={4} className="mb-4">
              <Card className="h-100 shadow-sm border-warning">
                <Card.Header className="bg-warning text-dark">
                  <FontAwesomeIcon icon={faGift} className="me-2" />
                  <strong>Beneficios</strong>
                </Card.Header>
                <Card.Body className="text-center">
                  <h2>
                    <Badge bg="success" className="p-3">
                      {stats.beneficios.activos} Activos
                    </Badge>
                  </h2>
                </Card.Body>
              </Card>
            </Col>

            {/* Sesiones */}
            <Col md={4} className="mb-4">
              <Card className="h-100 shadow-sm border-danger">
                <Card.Header className="bg-danger text-white">
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  <strong>Sesiones Activas</strong>
                </Card.Header>
                <Card.Body>
                  <div className="text-center mb-3">
                    <h2>
                      <Badge bg={stats.sesiones?.activas > 0 ? "success" : "secondary"} className="p-3">
                        {stats.sesiones?.activas || 0}
                      </Badge>
                    </h2>
                  </div>
                  <div className="d-grid">
                    <Button 
                      variant="outline-danger" 
                      onClick={handleCleanSessions}
                      disabled={cleaningSessions}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                      {cleaningSessions ? 'Limpiando...' : 'Limpiar Sesiones Caducadas'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )
      )}
    </div>
  );
};

export default DashboardPage;